import React, { useEffect } from 'react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Check, EyeOff, Pin } from 'lucide-react';

export type ColumnSortDirection = 'asc' | 'desc';

export interface ColumnHeaderMenuProps {
  title: string;
  description?: string;
  // Text columns read "Z to A" / "A to Z"; numeric ones "high to low" / "low to high".
  sortType: 'numeric' | 'text';
  // Direction this column is currently sorted in, or null when another column drives the sort.
  activeDirection: ColumnSortDirection | null;
  onSort: (direction: ColumnSortDirection) => void;
  // Omitted for the sticky label column, which can only be sorted.
  pin?: { isPinned: boolean; onToggle: () => void };
  onHide?: () => void;
  onClose: () => void;
  // Which header edge the card lines up with -- 'right' keeps the last columns' cards on screen.
  align?: 'left' | 'right';
}

// Each direction's wording, shared by the menu options and any "sorted by" summary.
export const SORT_DIRECTION_LABELS: Record<ColumnHeaderMenuProps['sortType'], Record<ColumnSortDirection, string>> = {
  numeric: { desc: 'high to low', asc: 'low to high' },
  text: { desc: 'Z to A', asc: 'A to Z' },
};

export const SORT_OPTION_LABELS: Record<ColumnHeaderMenuProps['sortType'], Record<ColumnSortDirection, string>> = {
  numeric: { desc: `Sort ${SORT_DIRECTION_LABELS.numeric.desc}`, asc: `Sort ${SORT_DIRECTION_LABELS.numeric.asc}` },
  text: { desc: `Sort ${SORT_DIRECTION_LABELS.text.desc}`, asc: `Sort ${SORT_DIRECTION_LABELS.text.asc}` },
};

const itemCss =
  'w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-[14px] font-medium text-[#344054] text-left hover:bg-[#F9FAFB] transition-colors cursor-pointer';
const iconCss = 'w-4 h-4 text-[#667085] shrink-0';
const checkCss = 'w-4 h-4 text-[#7C3AED] shrink-0 ml-auto';

/**
 * The card a column header opens: the column's name and description, sort high-to-low /
 * low-to-high, and (for metric columns) pin and hide. Closes on Escape; callers close it on an
 * outside click and after each action via onClose.
 */
export const ColumnHeaderMenu: React.FC<ColumnHeaderMenuProps> = ({
  title,
  description,
  sortType,
  activeDirection,
  onSort,
  pin,
  onHide,
  onClose,
  align = 'left',
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const sortItem = (direction: ColumnSortDirection, Icon: typeof ArrowDownWideNarrow) => (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={activeDirection === direction}
      className={itemCss}
      onClick={() => {
        onSort(direction);
        onClose();
      }}
    >
      <Icon className={iconCss} aria-hidden="true" />
      <span>{SORT_OPTION_LABELS[sortType][direction]}</span>
      {activeDirection === direction && <Check className={checkCss} aria-hidden="true" />}
    </button>
  );

  return (
    <div
      role="menu"
      aria-label={`${title} column menu`}
      // Clicks inside the card must not reach the header's own toggle or the row's handlers.
      onClick={(e) => e.stopPropagation()}
      className={`absolute top-full mt-1.5 ${align === 'right' ? 'right-0' : 'left-0'} w-64 bg-white rounded-2xl shadow-xl border border-[#EAECF0] p-4 text-left z-50 cursor-default whitespace-normal normal-case font-normal`}
    >
      <div className="px-2 pb-3">
        <div className="text-[14px] font-bold text-[#101828] leading-5">{title}</div>
        {description && <div className="mt-1 text-[12px] text-[#667085] leading-[18px]">{description}</div>}
      </div>
      <div className="border-t border-[#EAECF0] my-1.5" />
      {sortItem('desc', ArrowDownWideNarrow)}
      {sortItem('asc', ArrowUpNarrowWide)}
      {(pin || onHide) && <div className="border-t border-[#EAECF0] my-1.5" />}
      {pin && (
        <button
          type="button"
          role="menuitemcheckbox"
          aria-checked={pin.isPinned}
          className={itemCss}
          onClick={() => {
            pin.onToggle();
            onClose();
          }}
        >
          <Pin className={iconCss} aria-hidden="true" />
          <span>{pin.isPinned ? 'Unpin column' : 'Pin column'}</span>
          {pin.isPinned && <Check className={checkCss} aria-hidden="true" />}
        </button>
      )}
      {onHide && (
        <button
          type="button"
          role="menuitem"
          className={itemCss}
          onClick={() => {
            onHide();
            onClose();
          }}
        >
          <EyeOff className={iconCss} aria-hidden="true" />
          <span>Hide column</span>
        </button>
      )}
    </div>
  );
};

export default ColumnHeaderMenu;
